import {
  BoxGeometry,
  CylinderGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshStandardMaterial,
  PlaneGeometry,
  Vector3,
  type BufferGeometry,
  type Scene,
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { clamp, damp, smoothstep } from '../utils/rng';
import { segmentSphereHit } from '../utils/ballistics';
import { terrainHeight } from '../world/heightfield';
import { TARGET_SITES, type TargetSite } from '../world/rangeLayout';
import { targetFace } from '../world/textures';

/**
 * Les cibles.
 *
 * Trois familles, qui existent pour trois raisons de jeu differentes :
 *
 *   - `panel` : un panneau sur pieds. La cible d'etalonnage, visible de loin,
 *     qui apprend a lire une portee.
 *   - `moving` : le meme panneau sur un chariot qui va et vient. Il faut viser
 *     *devant*, parce que l'obus met deux a trois secondes a arriver.
 *   - `hulk` : une epave de blinde, plus trapue, placee derriere un merlon.
 *     Inatteignable en tir tendu : il faut monter la hausse.
 *
 * La detection utilise la distance d'un segment a une sphere (cf.
 * `segmentSphereHit`) et jamais de Raycaster : dix cibles, et un obus qui
 * avance de quatre metres par frame. cf. doc/04-balistique.md
 */

export interface Target {
  readonly site: TargetSite;
  readonly group: Group;
  /** Centre de la zone touchable, en coordonnees monde, remis a jour. */
  readonly center: Vector3;
  /** Rayon de la zone touchable, en metres. */
  readonly radius: number;
  hit: boolean;
  /** Avancement de la chute, de 0 (debout) a 1 (a terre). */
  fall: number;
  /** Deplacement lateral courant des cibles mobiles, en metres. */
  offset: number;
  /** Decalage de phase, pour que les cibles mobiles ne soient pas synchrones. */
  phase: number;
}

/** Rayon touchable selon la famille, en metres. */
const RADIUS: Record<TargetSite['kind'], number> = {
  panel: 1.5,
  moving: 1.5,
  hulk: 2.2,
};

/** Duree de la chute d'un panneau touche, en secondes. */
const FALL_TIME = 0.9;

/** Panneau de 2,6 m sur 2,6 m, sur deux pieds. */
function panelFrame(): BufferGeometry {
  const parts = [
    new CylinderGeometry(0.09, 0.11, 2.9, 7).translate(-1.15, 1.45, 0),
    new CylinderGeometry(0.09, 0.11, 2.9, 7).translate(1.15, 1.45, 0),
    // Jambe de force arriere : sans elle le panneau a l'air de tenir par magie.
    new CylinderGeometry(0.07, 0.07, 2.2, 6).rotateX(0.5).translate(0, 1.1, -0.5),
  ];
  return mergeGeometries(parts, false)!;
}

/**
 * Epave de blinde : caisse, superstructure et tourelle de travers.
 *
 * Le but n'est pas de modeliser un vrai vehicule : c'est une masse trapue
 * qu'on reconnait a 200 m comme « un engin », et dont la tourelle de travers
 * dit « hors de combat ».
 */
function hulkGeometry(): BufferGeometry {
  const parts = [
    new BoxGeometry(3.0, 0.9, 5.4).translate(0, 0.65, 0),
    new BoxGeometry(2.6, 0.7, 3.4).translate(0, 1.42, -0.3),
    new CylinderGeometry(1.0, 1.15, 0.7, 7).translate(0.15, 2.1, -0.5),
    // Tube pointe vers le bas et de cote.
    new CylinderGeometry(0.1, 0.12, 3.6, 8)
      .rotateX(Math.PI / 2)
      .rotateY(0.9)
      .rotateX(-0.22)
      .translate(1.2, 1.95, 0.8),
    // Chenilles rompues : deux blocs sombres au ras du sol.
    new BoxGeometry(0.6, 0.6, 5.2).translate(-1.5, 0.3, 0),
    new BoxGeometry(0.6, 0.6, 5.2).translate(1.5, 0.3, 0),
  ];
  return mergeGeometries(parts, false)!;
}

/** Chariot des cibles mobiles : un rail et un socle. */
function carriageGeometry(travel: number): BufferGeometry {
  const parts = [
    // Le rail court sur toute l'amplitude du va-et-vient.
    new BoxGeometry(travel * 2 + 2.4, 0.16, 0.5).translate(0, 0.08, 0),
    new BoxGeometry(1.6, 0.26, 0.9).translate(0, 0.3, 0),
  ];
  return mergeGeometries(parts, false)!;
}

export class Targets {
  readonly items: Target[] = [];
  destroyed = 0;

  /**
   * Horloge interne, avancee par `update`.
   *
   * Elle est ici plutot que dans `Game` parce que c'est elle qui permet de
   * repondre a « ou sera cette cible dans 3,2 secondes ? » -- la question que
   * pose tout calcul de tir sur un objectif mobile.
   */
  private elapsed = 0;

  private readonly faceMaterial: MeshStandardMaterial;
  private readonly frameMaterial = new MeshStandardMaterial({
    color: 0x6f6657,
    roughness: 0.9,
  });
  private readonly hulkMaterial = new MeshStandardMaterial({
    color: 0x5a5547,
    roughness: 0.92,
    metalness: 0.2,
    flatShading: true,
  });
  private readonly railMaterial = new MeshStandardMaterial({
    color: 0x4a4740,
    roughness: 0.7,
    metalness: 0.4,
  });

  /** Geometries partagees : construites une fois, instanciees par cible. */
  private readonly panelFace = new PlaneGeometry(2.6, 2.6);
  private readonly frame = panelFrame();
  private readonly hulk = hulkGeometry();

  constructor(scene: Scene) {
    this.faceMaterial = new MeshStandardMaterial({
      map: targetFace(),
      roughness: 0.82,
      // Le panneau est une surface sans epaisseur : il doit se voir des deux
      // cotes, sinon il disparait quand on le contourne.
      side: DoubleSide,
    });

    TARGET_SITES.forEach((site, i) => {
      const group = new Group();
      group.position.copy(site.position);
      group.rotation.y = site.yaw;

      if (site.kind === 'hulk') {
        const mesh = new Mesh(this.hulk, this.hulkMaterial);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        group.add(mesh);
      } else {
        if (site.kind === 'moving') {
          const rail = new Mesh(carriageGeometry(site.travel), this.railMaterial);
          rail.receiveShadow = true;
          group.add(rail);
        }
        // Le panneau et ses pieds sont portes par un sous-groupe : c'est lui
        // qui basculera quand la cible sera touchee, pas le rail.
        const panel = new Group();
        panel.name = 'panel';
        const frame = new Mesh(this.frame, this.frameMaterial);
        frame.castShadow = true;
        const face = new Mesh(this.panelFace, this.faceMaterial);
        face.position.set(0, 1.75, 0.08);
        face.castShadow = true;
        panel.add(frame, face);
        group.add(panel);
      }

      scene.add(group);
      this.items.push({
        site,
        group,
        center: new Vector3(),
        radius: RADIUS[site.kind],
        hit: false,
        fall: 0,
        offset: 0,
        // Un dephasage tire du rang, pas du hasard : le champ de tir est
        // identique d'une partie a l'autre.
        phase: (i * 2.39996) % (Math.PI * 2),
      });
    });

    // Les centres doivent etre valides avant le premier tir, meme si aucune
    // frame n'a encore tourne.
    this.update(0);
  }

  update(dt: number): void {
    this.elapsed += dt;
    for (const t of this.items) {
      if (t.site.kind === 'moving' && !t.hit) {
        t.offset = this.offsetAt(t, 0);
        const panel = t.group.getObjectByName('panel');
        if (panel) {
          panel.position.x = t.offset;
          // Le chariot suit le sol : le rail est droit, le terrain non.
          const s = Math.sin(t.site.yaw);
          const c = Math.cos(t.site.yaw);
          const wx = t.site.position.x + t.offset * c;
          const wz = t.site.position.z - t.offset * s;
          panel.position.y = terrainHeight(wx, wz) - t.site.position.y;
        }
      }

      if (t.hit && t.fall < 1) {
        t.fall = clamp(t.fall + dt / FALL_TIME, 0, 1);
        const panel = t.group.getObjectByName('panel');
        if (panel) {
          // Bascule vers l'arriere, en accelerant : une chute, pas une
          // interpolation lineaire.
          panel.rotation.x = -(Math.PI / 2) * smoothstep(0, 1, t.fall) * t.fall;
        } else {
          // Une epave ne tombe pas : elle s'affaisse et s'incline.
          const mesh = t.group.children[0];
          if (mesh) {
            mesh.rotation.z = damp(mesh.rotation.z, 0.22, 3, dt);
            mesh.position.y = damp(mesh.position.y, -0.35, 3, dt);
          }
        }
      }

      this.refreshCenter(t);
    }
  }

  /**
   * Position laterale d'une cible mobile dans `ahead` secondes.
   *
   * Va-et-vient sinusoidal : la vitesse s'annule aux extremites, ce qui donne
   * au joueur une fenetre de tir plus facile aux deux bouts. Le fait que ce
   * soit une fonction fermee du temps -- et non une position integree -- est
   * ce qui rend le calcul d'avance possible : on peut demander la position
   * future sans simuler.
   */
  private offsetAt(t: Target, ahead: number): number {
    if (t.site.kind !== 'moving' || t.hit) return t.offset;
    const w = (Math.PI * 2) / t.site.period;
    return Math.sin((this.elapsed + ahead) * w + t.phase) * t.site.travel;
  }

  /**
   * Ou sera le centre de la cible `index` dans `ahead` secondes.
   *
   * C'est ce qu'il faut viser pour toucher un chariot : a 220 m l'obus met
   * trois secondes a arriver, et la cible a parcouru vingt metres pendant ce
   * temps. Un calculateur de tir reel resout la meme equation implicite --
   * le temps de vol depend de la distance, qui depend de l'avance, qui depend
   * du temps de vol -- par quelques iterations.
   */
  predictCenter(index: number, ahead: number, out: Vector3): Vector3 | null {
    const t = this.items[index];
    if (!t) return null;
    const offset = this.offsetAt(t, ahead);
    const s = Math.sin(t.site.yaw);
    const c = Math.cos(t.site.yaw);
    const x = t.site.position.x + offset * c;
    const z = t.site.position.z - offset * s;
    const groundY = t.site.kind === 'moving' ? terrainHeight(x, z) : t.site.position.y;
    return out.set(x, groundY + (t.site.kind === 'hulk' ? 1.3 : 1.75), z);
  }

  /**
   * Centre de la zone touchable.
   *
   * Pour un panneau c'est le milieu de la plaque, a 1,75 m du sol -- pas le
   * pied du poteau. Viser le pied et compter un coup au but serait faux dans
   * les deux sens : on toucherait en passant sous la cible, et on raterait en
   * touchant le centre du panneau.
   */
  private refreshCenter(t: Target): void {
    const s = Math.sin(t.site.yaw);
    const c = Math.cos(t.site.yaw);
    const x = t.site.position.x + t.offset * c;
    const z = t.site.position.z - t.offset * s;
    const groundY = t.site.kind === 'moving' ? terrainHeight(x, z) : t.site.position.y;
    t.center.set(x, groundY + (t.site.kind === 'hulk' ? 1.3 : 1.75), z);
  }

  /**
   * Premiere cible debout coupee par le segment [from, to], ou null.
   *
   * On renvoie un indice plutot qu'un objet : `Shells` n'a alors rien a savoir
   * des cibles, et le couplage reste a sens unique.
   */
  hitBy(from: Vector3, to: Vector3): number | null {
    for (let i = 0; i < this.items.length; i++) {
      const t = this.items[i]!;
      if (t.hit) continue;
      if (segmentSphereHit(from, to, t.center, t.radius)) return i;
    }
    return null;
  }

  /** Marque une cible comme detruite. Renvoie false si elle l'etait deja. */
  destroy(index: number): boolean {
    const t = this.items[index];
    if (!t || t.hit) return false;
    t.hit = true;
    t.fall = 0;
    this.destroyed++;
    return true;
  }

  /** Cible debout la plus proche d'un point, pour l'aide a la visee. */
  nearest(from: Vector3): { target: Target; distance: number } | null {
    let best: Target | null = null;
    let bestD = Infinity;
    for (const t of this.items) {
      if (t.hit) continue;
      const d = t.center.distanceToSquared(from);
      if (d < bestD) {
        bestD = d;
        best = t;
      }
    }
    return best ? { target: best, distance: Math.sqrt(bestD) } : null;
  }

  get total(): number {
    return this.items.length;
  }

  get done(): boolean {
    return this.destroyed >= this.items.length;
  }

  reset(): void {
    this.destroyed = 0;
    this.elapsed = 0;
    for (const t of this.items) {
      t.hit = false;
      t.fall = 0;
      t.offset = 0;
      const panel = t.group.getObjectByName('panel');
      if (panel) {
        panel.rotation.x = 0;
        panel.position.x = 0;
      } else {
        const mesh = t.group.children[0];
        if (mesh) {
          mesh.rotation.z = 0;
          mesh.position.y = 0;
        }
      }
      this.refreshCenter(t);
    }
  }
}
